/**
 * Synergy Wholesale bulk import wizard.
 *
 * Three-step flow:
 *   1. Connect — verify credentials with /api/domains/synergy/test, then load
 *      one page of /api/domains/synergy/list and show domain rows annotated
 *      with their current SonOps account assignment.
 *   2. Assign — select rows to import, pick the destination Account per row
 *      (or use the "same account for all" shortcut).
 *   3. Confirm — POST /api/domains/synergy/import and surface per-row results.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CloudDownload as DownloadIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import {
  SynergyDomainListItem,
  SynergyImportItemResult,
  SynergyTestConnectionResponse,
} from '../../types/domainRegistry';

interface AccountOption {
  id: string;
  label: string;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'ok', label: 'OK' },
  { value: 'clientTransferProhibited', label: 'Transfer locked' },
  { value: 'clienthold', label: 'Client hold' },
  { value: 'redemption', label: 'Redemption' },
  { value: 'pendingDelete', label: 'Pending delete' },
];

function formatDate(value?: string | null): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return value;
  }
}

function resultColor(s: SynergyImportItemResult['status']): 'success' | 'info' | 'warning' | 'error' {
  if (s === 'created') return 'success';
  if (s === 'updated') return 'info';
  if (s === 'skipped') return 'warning';
  return 'error';
}

const SynergyImportPage: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Step 0: connection test
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<SynergyTestConnectionResponse | null>(null);

  // Step 1: domain list
  const [domains, setDomains] = useState<SynergyDomainListItem[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loadingDomains, setLoadingDomains] = useState(false);

  // Step 2: assignment
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [assignments, setAssignments] = useState<Record<string, AccountOption | null>>({});
  const [bulkAccount, setBulkAccount] = useState<AccountOption | null>(null);
  const [accountQuery, setAccountQuery] = useState('');
  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
  const [accountLoading, setAccountLoading] = useState(false);

  // Step 3: import results
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<SynergyImportItemResult[] | null>(null);

  const handleTestConnection = useCallback(async () => {
    setTesting(true);
    setError(null);
    try {
      const r = await apiService.testSynergyConnection();
      setTestResult(r);
      if (!r.ok) setError(r.message || 'Connection failed');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(e?.response?.data?.detail || e?.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  }, []);

  const loadDomains = useCallback(
    async (opts?: { page?: number; status?: string; limit?: number }) => {
      setLoadingDomains(true);
      setError(null);
      try {
        const data = await apiService.listSynergyDomains({
          page: opts?.page ?? page,
          limit: opts?.limit ?? limit,
          status: opts?.status ?? (statusFilter || undefined),
        });
        setDomains(data.items || []);
      } catch (err: unknown) {
        const e = err as { response?: { data?: { detail?: string } }; message?: string };
        setError(e?.response?.data?.detail || e?.message || 'Failed to load domains');
        setDomains([]);
      } finally {
        setLoadingDomains(false);
      }
    },
    [page, limit, statusFilter],
  );

  useEffect(() => {
    if (activeStep === 0 && !testResult) {
      void handleTestConnection();
    }
  }, [activeStep, testResult, handleTestConnection]);

  useEffect(() => {
    if (activeStep === 1 && domains.length === 0 && testResult?.ok) {
      void loadDomains({ page: 1 });
    }
  }, [activeStep, domains.length, testResult, loadDomains]);

  // Account autocomplete search.
  useEffect(() => {
    let cancelled = false;
    const id = setTimeout(async () => {
      setAccountLoading(true);
      try {
        const data = await apiService.getAccounts({ q: accountQuery || '', per_page: 25 });
        if (cancelled) return;
        const items: AccountOption[] = (data?.accounts || []).map((a: any) => ({
          id: a.id,
          label:
            a.organisation_name ||
            a.full_name ||
            `${a.contact_first_name ?? ''} ${a.contact_last_name ?? ''}`.trim() ||
            a.sonic_account_number ||
            a.id,
        }));
        setAccountOptions(items);
      } catch {
        if (!cancelled) setAccountOptions([]);
      } finally {
        if (!cancelled) setAccountLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [accountQuery]);

  const selectableDomains = useMemo(
    () => domains.filter((d) => !d.already_attached),
    [domains],
  );

  const selectedDomains = useMemo(
    () => domains.filter((d) => selected[d.domain_name]),
    [domains, selected],
  );

  const allSelectableSelected = useMemo(
    () =>
      selectableDomains.length > 0 &&
      selectableDomains.every((d) => selected[d.domain_name]),
    [selectableDomains, selected],
  );

  const someSelected = useMemo(
    () => selectableDomains.some((d) => selected[d.domain_name]),
    [selectableDomains, selected],
  );

  const toggleAll = () => {
    if (allSelectableSelected) {
      setSelected({});
      return;
    }
    const next: Record<string, boolean> = {};
    selectableDomains.forEach((d) => {
      next[d.domain_name] = true;
    });
    setSelected(next);
  };

  const applyBulkAccount = () => {
    if (!bulkAccount) return;
    const next = { ...assignments };
    selectedDomains.forEach((d) => {
      next[d.domain_name] = bulkAccount;
    });
    setAssignments(next);
    setInfo(`Applied account "${bulkAccount.label}" to ${selectedDomains.length} selected row(s).`);
  };

  const canAdvanceFromStep1 = selectedDomains.length > 0;
  const allAssigned = selectedDomains.every((d) => assignments[d.domain_name]);

  const handleSubmitImport = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const items = selectedDomains
        .filter((d) => assignments[d.domain_name])
        .map((d) => ({
          domain_name: d.domain_name,
          account_id: assignments[d.domain_name]!.id,
        }));
      const res = await apiService.importSynergyDomains(items);
      setResults(res.results);
      setActiveStep(3);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(e?.response?.data?.detail || e?.message || 'Import failed');
    } finally {
      setSubmitting(false);
    }
  }, [assignments, selectedDomains]);

  const reset = () => {
    setActiveStep(0);
    setTestResult(null);
    setDomains([]);
    setSelected({});
    setAssignments({});
    setBulkAccount(null);
    setResults(null);
    setError(null);
    setInfo(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button
          component={RouterLink}
          to="/settings"
          startIcon={<BackIcon />}
          variant="text"
          size="small"
        >
          Back to Settings
        </Button>
      </Stack>

      <Typography variant="h4" gutterBottom>
        Import domains from Synergy Wholesale
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Bulk-attach Synergy Wholesale domains to SonOps accounts as Domain Name services.
        Each imported domain is synced immediately so the registry data is ready to use.
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        <Step>
          <StepLabel>Connect</StepLabel>
        </Step>
        <Step>
          <StepLabel>Choose domains</StepLabel>
        </Step>
        <Step>
          <StepLabel>Assign accounts</StepLabel>
        </Step>
        <Step>
          <StepLabel>Done</StepLabel>
        </Step>
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      {activeStep === 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Verify Synergy connection
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Synergy Wholesale credentials are read from{' '}
            <code>SYNERGY_RESELLER_ID</code> and <code>SYNERGY_API_KEY</code> on the backend.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="contained"
              startIcon={testing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              onClick={handleTestConnection}
              disabled={testing}
            >
              {testResult ? 'Re-test connection' : 'Test connection'}
            </Button>
            {testResult && (
              <Chip
                color={testResult.ok ? 'success' : 'error'}
                label={
                  testResult.ok
                    ? testResult.balance
                      ? `Connected (balance ${testResult.balance})`
                      : 'Connected'
                    : `Failed: ${testResult.message}`
                }
              />
            )}
          </Stack>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              disabled={!testResult?.ok}
              onClick={() => setActiveStep(1)}
            >
              Continue
            </Button>
          </Box>
        </Paper>
      )}

      {activeStep === 1 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status filter"
                onChange={(e) => setStatusFilter(String(e.target.value))}
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value || 'all'} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Page</InputLabel>
              <Select
                value={page}
                label="Page"
                onChange={(e) => setPage(Number(e.target.value))}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Limit</InputLabel>
              <Select
                value={limit}
                label="Limit"
                onChange={(e) => setLimit(Number(e.target.value))}
              >
                {[50, 100, 200, 500].map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              startIcon={loadingDomains ? <CircularProgress size={14} /> : <RefreshIcon />}
              onClick={() => loadDomains({ page, status: statusFilter, limit })}
              disabled={loadingDomains}
            >
              Reload
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {selectedDomains.length} of {selectableDomains.length} unassigned domains selected
            </Typography>
          </Stack>

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={someSelected && !allSelectableSelected}
                      checked={allSelectableSelected}
                      onChange={toggleAll}
                      disabled={selectableDomains.length === 0}
                    />
                  </TableCell>
                  <TableCell>Domain</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell>ID Protect</TableCell>
                  <TableCell>Auto-renew</TableCell>
                  <TableCell>SonOps account</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingDomains && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress size={20} />
                    </TableCell>
                  </TableRow>
                )}
                {!loadingDomains && domains.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">No domains returned for this page.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loadingDomains &&
                  domains.map((d) => {
                    const disabled = d.already_attached;
                    return (
                      <TableRow
                        key={d.domain_name}
                        hover
                        sx={disabled ? { opacity: 0.55 } : undefined}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={!!selected[d.domain_name]}
                            disabled={disabled}
                            onChange={(e) =>
                              setSelected((prev) => ({ ...prev, [d.domain_name]: e.target.checked }))
                            }
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{d.domain_name}</TableCell>
                        <TableCell>{d.domain_status || d.status || '—'}</TableCell>
                        <TableCell>{formatDate(d.expiry_at)}</TableCell>
                        <TableCell>{d.id_protect || '—'}</TableCell>
                        <TableCell>{d.auto_renew || '—'}</TableCell>
                        <TableCell>
                          {d.already_attached ? (
                            <Chip
                              size="small"
                              color="success"
                              label={`Attached: ${d.attached_account_display_name || d.attached_account_id}`}
                            />
                          ) : (
                            <Chip size="small" variant="outlined" label="Not attached" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={() => setActiveStep(0)}>Back</Button>
            <Button
              variant="contained"
              disabled={!canAdvanceFromStep1}
              onClick={() => setActiveStep(2)}
            >
              Next: assign accounts
            </Button>
          </Box>
        </Paper>
      )}

      {activeStep === 2 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Pick a destination account for each domain
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each selected Synergy domain will be added (or moved) to the chosen SonOps account as a
            Domain Name service, then synced immediately.
          </Typography>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} md={8}>
              <Autocomplete
                options={accountOptions}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={bulkAccount}
                loading={accountLoading}
                onChange={(_, v) => setBulkAccount(v)}
                onInputChange={(_, v) => setAccountQuery(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Same account for all selected domains" size="small" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="outlined"
                disabled={!bulkAccount || selectedDomains.length === 0}
                onClick={applyBulkAccount}
              >
                Apply to all selected
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Domain</TableCell>
                  <TableCell>Synergy status</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell sx={{ minWidth: 320 }}>Destination account</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedDomains.map((d) => {
                  const value = assignments[d.domain_name] || null;
                  return (
                    <TableRow key={d.domain_name}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{d.domain_name}</TableCell>
                      <TableCell>{d.domain_status || d.status || '—'}</TableCell>
                      <TableCell>{formatDate(d.expiry_at)}</TableCell>
                      <TableCell>
                        <Autocomplete
                          options={accountOptions}
                          getOptionLabel={(o) => o.label}
                          isOptionEqualToValue={(a, b) => a.id === b.id}
                          value={value}
                          loading={accountLoading}
                          onChange={(_, v) =>
                            setAssignments((prev) => ({ ...prev, [d.domain_name]: v }))
                          }
                          onInputChange={(_, v) => setAccountQuery(v)}
                          renderInput={(params) => (
                            <TextField {...params} label="Account" size="small" required />
                          )}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={() => setActiveStep(1)}>Back</Button>
            <Button
              variant="contained"
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={handleSubmitImport}
              disabled={submitting || !allAssigned || selectedDomains.length === 0}
            >
              {submitting ? 'Importing…' : `Import ${selectedDomains.length} domain(s)`}
            </Button>
          </Box>
        </Paper>
      )}

      {activeStep === 3 && results && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Import complete
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Chip color="success" icon={<CheckIcon />} label={`${results.filter((r) => r.status === 'created').length} created`} />
            <Chip color="info" label={`${results.filter((r) => r.status === 'updated').length} updated`} />
            <Chip color="warning" label={`${results.filter((r) => r.status === 'skipped').length} skipped`} />
            <Chip color="error" label={`${results.filter((r) => r.status === 'error').length} errors`} />
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Domain</TableCell>
                <TableCell>Result</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((r) => (
                <TableRow key={`${r.domain_name}-${r.account_id}`}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{r.domain_name}</TableCell>
                  <TableCell>
                    <Chip size="small" color={resultColor(r.status)} label={r.status} />
                  </TableCell>
                  <TableCell>
                    {r.account_service_id ? (
                      <Button
                        component={RouterLink}
                        to={`/accounts/${r.account_id}?service=${r.account_service_id}`}
                        size="small"
                      >
                        Open
                      </Button>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320, wordBreak: 'break-word' }}>
                    {r.error_message || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={reset}>Import more domains</Button>
            <Button component={RouterLink} to="/settings" variant="contained">
              Back to Settings
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default SynergyImportPage;
